import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

const Bar = styled.div`
  padding: 70px 80px;
  background: linear-gradient(135deg, rgba(0,163,255,0.05) 0%, rgba(107,0,255,0.05) 100%);
  border-top: 1px solid rgba(0,163,255,0.1);
  border-bottom: 1px solid rgba(0,163,255,0.1);

  @media (max-width: 900px) {
    padding: 50px 24px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  max-width: 1360px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Item = styled.div`
  text-align: center;
  padding: 32px 16px;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 15%;
    height: 70%;
    width: 1px;
    background: rgba(255,255,255,0.06);
  }
`;

const Num = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(60px, 8vw, 90px);
  line-height: 1;
  background: ${({ theme }) => theme.gradients.brand};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  transition: filter 0.3s;

  ${Item}:hover & {
    filter: drop-shadow(0 0 18px rgba(0,163,255,0.5));
  }
`;

const Label = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
  font-weight: 600;
`;

function CountUp({ target, duration = 1800, suffix = '' }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current || isNaN(target)) return;
    started.current = true;
    let start = 0;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(ease * target));
      if (p < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);

  if (isNaN(target)) return <span ref={ref}>{target}{suffix}</span>;
  return <span ref={ref}>{count}{suffix}</span>;
}

const statsNums = [
  { num: 11,   suffix: '' },
  { num: 9,    suffix: '+' },
  { num: 420,  suffix: '+' },
  { num: 6,    suffix: '' },
];

export default function Stats() {
  const { t } = useTranslation();
  const statsLabels = t('stats.items', { returnObjects: true });

  return (
    <Bar>
      <Grid>
        {statsNums.map((s, i) => (
          <Item key={i}>
            <Num>
              <CountUp target={s.num} suffix={s.suffix} />
            </Num>
            <Label>{statsLabels[i].label}</Label>
          </Item>
        ))}
      </Grid>
    </Bar>
  );
}
